/**
 * G1-T3.7: JWT token'dan kullanıcı bilgilerini ve rollerini okur.
 * Herhangi bir kütüphane gerektirmeden base64 ile decode eder.
 */

export function getToken() {
  return localStorage.getItem('token');
}

export function getTokenPayload() {
  const token = getToken();
  if (!token) return null;
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

// #259 JWT "exp" claim'i (saniye) → ms cinsinden mutlak son kullanma anı; yoksa null.
export function getTokenExp() {
  const payload = getTokenPayload();
  if (!payload || payload.exp == null) return null;
  const seconds = Number(payload.exp);
  return Number.isFinite(seconds) ? seconds * 1000 : null;
}

// #259 Token'ın mutlak süresi dolmuş mu? (exp yoksa "dolmamış" sayılır.)
export function isTokenExpired() {
  const exp = getTokenExp();
  return exp != null && Date.now() >= exp;
}

export function getUserRoles() {
  const payload = getTokenPayload();
  if (!payload) return [];
  // ASP.NET Core ClaimTypes.Role → "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
  const roleKey = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';
  const raw = payload[roleKey] ?? payload['role'] ?? [];
  return Array.isArray(raw) ? raw : [raw];
}

export function hasRole(role) {
  return getUserRoles().includes(role);
}

export function isSalesRep() {
  return hasRole('SALES_REP');
}

// SuperAdmin: tenant-ötesi tenant yönetimi yetkisi (JwtHelper "isSuperAdmin" claim'i veya SUPERADMIN rolü).
export function isSuperAdmin() {
  const payload = getTokenPayload();
  if (payload && (payload['isSuperAdmin'] === 'true' || payload['isSuperAdmin'] === true)) return true;
  return hasRole('SUPERADMIN');
}

// ADMIN grubu: backend admini Role.Code = "01" (Name "Yönetici") olarak seed eder ve JWT rol
// claim'i role.Code taşır — bu yüzden gerçek adminler "01" rolü ile gelir. Eski "ADMIN" kodu ve
// SUPERADMIN da admin sayılır (backend GetMyMenuQueryHandler / CustomerScopeFilter ile aynı mantık).
export function isAdmin() {
  const roles = getUserRoles();
  return roles.includes('01') || roles.includes('ADMIN') || isSuperAdmin();
}

// Oturumdaki kullanıcının Id'si (JWT "UserId" claim'i). Kendi hesabını düzenleme
// kontrolü gibi yerlerde kullanılır. Sayı olarak döner; yoksa null.
export function getUserId() {
  const payload = getTokenPayload();
  if (!payload) return null;
  const raw = payload['UserId'] ?? payload['nameid'] ??
    payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'];
  const id = Number(raw);
  return Number.isFinite(id) ? id : null;
}

export function getUsername() {
  const payload = getTokenPayload();
  if (!payload) return null;
  return (
    payload['unique_name'] ??
    payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] ??
    payload['name'] ??
    payload['email'] ??
    payload['sub'] ??
    null
  );
}

export function getTenantName() {
  const payload = getTokenPayload();
  if (!payload) return null;
  return payload['TenantName'] ?? null;
}

export function getLanguageCode() {
  const payload = getTokenPayload();
  if (!payload) return 'tr-TR';
  return payload['LanguageCode'] ?? 'tr-TR';
}

export function getRoleLabel(role) {
  const labels = {
    '01': 'Yönetici',
    '02': 'Müdür',
    ADMIN: 'Admin',
    MANAGER: 'Yönetici',
    SALES_REP: 'Satış Temsilcisi',
    RULE_MANAGER: 'Kural Yöneticisi',
    SUPERADMIN: 'Süper Yönetici',
  };
  return labels[role] ?? role;
}

export function getAuthHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}
